import { useState, useEffect, useCallback } from "react";
import { fetchAPI } from "./utils";
import {
	isPushSupported,
	hasLocalPushSubscription,
	subscribeUserToPush,
	syncLocalPushSubscriptionToServer,
} from "./pushNotifications";

const INITIAL_STATE = {
	loading: true,
	canPrompt: false,
	dismissed: false,
	enabled: false,
};

/**
 * Centralizes the push opt-in onboarding state shared by the dashboard modal
 * and the Messages reminder banner.
 *
 * - `canPrompt`: push is supported + server configured AND the user never
 *   enabled push (and has no live local subscription).
 * - `dismissed`: the user already closed the modal once (persisted server-side
 *   via `push_prompt_dismissed_at`), so only the banner should appear.
 *
 * Show the modal when `canPrompt && !dismissed`; show the banner when
 * `canPrompt && dismissed`.
 */
export function usePushOptIn() {
	const [state, setState] = useState(INITIAL_STATE);

	const evaluate = useCallback(async () => {
		if (!isPushSupported()) {
			setState({ ...INITIAL_STATE, loading: false });
			return;
		}
		try {
			const [prefs, cfg, localSub] = await Promise.all([
				fetchAPI("/users/me/notifications").catch(() => null),
				fetchAPI("/users/me/push-config").catch(() => null),
				hasLocalPushSubscription().catch(() => false),
			]);
			const canPush = !!cfg?.push_available;
			const enabled =
				!!prefs?.push_enabled || !!prefs?.push_ever_enabled || !!localSub;
			const dismissed = !!prefs?.push_prompt_dismissed_at;
			// Self-heal: if a local browser subscription exists but the server
			// may not have it (e.g. the initial save failed with a Safari/iOS
			// "Load failed"), re-sync it so notifications actually get delivered.
			if (localSub && canPush) {
				syncLocalPushSubscriptionToServer().catch(() => {});
			}
			setState({
				loading: false,
				canPrompt: canPush && !enabled,
				dismissed,
				enabled,
			});
		} catch {
			setState({ ...INITIAL_STATE, loading: false });
		}
	}, []);

	useEffect(() => {
		evaluate();
		const handler = () => evaluate();
		window.addEventListener("push-onboarding-updated", handler);
		return () => window.removeEventListener("push-onboarding-updated", handler);
	}, [evaluate]);

	// Single click → native browser permission prompt → subscription saved.
	const enable = useCallback(async () => {
		await subscribeUserToPush();
		window.dispatchEvent(new Event("push-onboarding-updated"));
	}, []);

	// Persist the dismissal so the modal does not resurface (cross-device).
	const dismiss = useCallback(async () => {
		await fetchAPI("/users/me/push-prompt-dismiss", { method: "POST" }).catch(
			() => {},
		);
		setState((prev) => ({ ...prev, dismissed: true }));
		window.dispatchEvent(new Event("push-onboarding-updated"));
	}, []);

	return { ...state, refresh: evaluate, enable, dismiss };
}
